// Marketplace Administration Service Layer
// Feature: 006-marketplace-management
// Updated: 007-cloudflare-migration - migrated to Cloudflare API Gateway

import * as Sentry from '@sentry/react';
import {
  listListings,
  getListing,
  updateListing,
  getUser,
  getFraudStats,
  listFlagged,
  listBlocked,
  blockEntity,
  unblockEntity,
  reviewFlag,
  getFraudConfig,
  updateFraudConfig,
  getCategories,
} from './api/admin';
import { uploadImages, deleteImages, extractImageKey } from './api/images';
import { isApiError } from './api/errors';
import type {
  ApiListing,
  ApiListingListItem,
  ListingListParams,
  PaginatedResponse,
  ApiFraudStats,
  ApiFraudFlag,
  ApiFraudBlock,
  FraudConfig,
  ReviewAction,
} from './api/types';
import type {
  MarketplaceItemSummary,
  MarketplaceItemWithSeller,
  MarketplaceItemStatus,
  MarketplaceItemUpdate,
  CategoryOption,
  PaginatedItemsResponse,
  ServiceResult,
} from '../types/marketplaceAdmin';

/**
 * Default page size for pagination
 */
const DEFAULT_PAGE_SIZE = 50;

/**
 * Transform API listing list item to legacy MarketplaceItemSummary format
 */
function transformListingListItem(apiListing: ApiListingListItem): MarketplaceItemSummary {
  return {
    id: apiListing.id,
    title: apiListing.title,
    price: apiListing.price,
    status: apiListing.status,
    category: apiListing.category_name || 'Uncategorized',
    categoryId: apiListing.category_id,
    sellerEmail: undefined, // Not available in list view
    photoUrl: apiListing.photos?.[0] ?? null,
    createdAt: apiListing.created_at,
    expiresAt: apiListing.expires_at,
  };
}

/**
 * Transform API listing to legacy MarketplaceItemWithSeller format
 */
function transformListing(apiListing: ApiListing): MarketplaceItemWithSeller {
  // Convert location object to a simple address string if available
  const locationString = apiListing.location?.address || apiListing.location?.city || undefined;
  
  return {
    id: apiListing.id,
    seller_id: apiListing.user_id,
    category_id: apiListing.category_id,
    data: {
      title: apiListing.title,
      title_km: apiListing.title_km,
      description: apiListing.description ?? null,
      description_km: apiListing.description_km,
      price: apiListing.price,
      status: apiListing.status,
      photos: apiListing.photos?.map((url) => ({ url, thumbnail_url: url })) || [],
      location: locationString,
      latitude: apiListing.latitude ?? apiListing.location?.lat,
      longitude: apiListing.longitude ?? apiListing.location?.lng,
      tags: apiListing.tags || [],
      offer_enabled: apiListing.offer_enabled ?? false,
      offer_min: apiListing.offer_min,
      offer_max: apiListing.offer_max,
      ai_generated: apiListing.ai_generated ?? false,
    },
    created_at: apiListing.created_at,
    updated_at: apiListing.updated_at,
    expires_at: apiListing.expires_at,
    sellerEmail: undefined,
    sellerDeleted: false,
  };
}

/**
 * Get all marketplace items with search, filter, and pagination support
 */
export async function getAllMarketplaceItems(
  searchTerm: string = '',
  statusFilter: MarketplaceItemStatus | 'all' = 'all',
  categoryFilter: string = 'all',
  page: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<PaginatedItemsResponse> {
  try {
    const params: ListingListParams = {
      limit: pageSize,
    };

    if (searchTerm) {
      params.q = searchTerm;
    }

    if (statusFilter !== 'all') {
      params.status = statusFilter as any;
    }

    if (categoryFilter !== 'all') {
      params.category = categoryFilter;
    }

    const response: PaginatedResponse<ApiListingListItem> = await listListings(params);

    const items = response.data.map(transformListingListItem);
    const totalCount = response.pagination.total;
    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      items,
      totalCount,
      currentPage: page,
      totalPages,
      hasMore: response.pagination.hasMore,
    };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getAllMarketplaceItems' },
        contexts: {
          query: { searchTerm, statusFilter, categoryFilter, page, pageSize },
        },
      });
    }
    throw error;
  }
}

/**
 * Get complete details for a specific marketplace item
 * Also fetches seller email from user profile
 */
export async function getMarketplaceItemDetails(itemId: string): Promise<MarketplaceItemWithSeller | null> {
  try {
    const apiListing = await getListing(itemId);
    const item = transformListing(apiListing);
    
    // Fetch seller info if user_id is available
    if (apiListing.user_id) {
      try {
        const seller = await getUser(apiListing.user_id);
        item.sellerEmail = seller.email;
        item.sellerDeleted = seller.status === 'deleted';
      } catch {
        // Seller may have been deleted, leave as undefined
        item.sellerDeleted = true;
      }
    }
    
    return item;
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getMarketplaceItemDetails' },
        extra: { itemId },
      });
    }
    return null;
  }
}

/**
 * Change marketplace item status
 * Valid transitions:
 * - active -> deleted (requires reason)
 * - active -> expired
 * - deleted -> active (restore)
 * - expired -> active (reactivate)
 */
export async function changeItemStatus(
  itemId: string,
  newStatus: MarketplaceItemStatus,
  reason?: string
): Promise<ServiceResult<void>> {
  try {
    // Validate reason is provided when deleting
    if (newStatus === 'deleted' && !reason) {
      return { success: false, error: 'Reason is required when deleting an item' };
    }

    await updateListing(itemId, { status: newStatus });

    // Log status change for audit trail
    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: `Marketplace item status changed to ${newStatus}`,
      level: 'info',
      data: { itemId, newStatus, reason: reason || 'none' },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'changeItemStatus' },
        extra: { itemId, newStatus, reason },
      });
    }
    return { success: false, error: 'Failed to change item status' };
  }
}

/**
 * Update marketplace item profile information
 */
export async function updateMarketplaceItem(
  itemId: string,
  updates: MarketplaceItemUpdate
): Promise<ServiceResult<void>> {
  try {
    // Build the update request with all supported fields
    const updateRequest: Parameters<typeof updateListing>[1] = {
      title: updates.title,
      description: updates.description,
      price: updates.price,
      category_id: updates.category_id,
      tags: updates.tags,
      offer_enabled: updates.offer_enabled,
      offer_min: updates.offer_min,
      offer_max: updates.offer_max,
    };

    // Add location if coordinates are provided
    if (updates.latitude !== undefined && updates.longitude !== undefined) {
      if (updates.latitude === null || updates.longitude === null) {
        updateRequest.location = null;
      } else {
        updateRequest.location = {
          lat: updates.latitude,
          lng: updates.longitude,
        };
      }
    }

    // Add city/location string if provided
    if (updates.location !== undefined) {
      updateRequest.city = updates.location ?? undefined;
    }

    await updateListing(itemId, updateRequest);

    // Log profile update for audit trail
    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: 'Marketplace item updated',
      level: 'info',
      data: { itemId, updatedFields: Object.keys(updates) },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'updateMarketplaceItem' },
        extra: { itemId, updatedFields: Object.keys(updates) },
      });
    }
    return { success: false, error: 'Failed to update marketplace item' };
  }
}

/**
 * Add photos to a marketplace item using R2 Image Handler API
 */
export async function addItemPhotos(
  itemId: string,
  files: File[]
): Promise<ServiceResult<string[]>> {
  try {
    const uploadResult = await uploadImages(files, 'marketplace');
    
    if (!uploadResult.success || uploadResult.errors.length > 0) {
      const errorMsg = uploadResult.errors[0]?.error || 'Failed to upload photos';
      return { success: false, error: errorMsg };
    }
    
    // Collect the public URLs from uploaded images
    const uploadedUrls = uploadResult.images.map(img => img.publicUrl);

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: 'Photos uploaded to R2',
      level: 'info',
      data: {
        itemId,
        photoCount: uploadedUrls.length,
      },
    });

    return { success: true, data: uploadedUrls };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'addItemPhotos' },
        extra: { itemId, fileCount: files.length },
      });
    }
    return { success: false, error: 'Failed to upload photos' };
  }
}

/**
 * Remove photos from a marketplace item using R2 Image Handler API
 */
export async function removeItemPhotos(
  itemId: string,
  photoUrls: string[]
): Promise<ServiceResult<void>> {
  try {
    // Extract keys from URLs
    const keysToDelete = photoUrls
      .map(url => extractImageKey(url))
      .filter((key): key is string => key !== null);
    
    if (keysToDelete.length === 0) {
      return { success: true };
    }

    const deleteResult = await deleteImages(keysToDelete);
    
    if (!deleteResult.success) {
      Sentry.addBreadcrumb({
        category: 'marketplace-admin',
        message: `Some photos failed to delete: ${deleteResult.failed.length} failed`,
        level: 'warning',
        data: { itemId, failed: deleteResult.failed },
      });
      // Continue anyway - photos might already be deleted
    }

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: 'Photos removed from R2',
      level: 'info',
      data: {
        itemId,
        deletedCount: deleteResult.deleted.length,
      },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'removeItemPhotos' },
        extra: { itemId, photoCount: photoUrls.length },
      });
    }
    return { success: false, error: 'Failed to remove photos' };
  }
}

/**
 * Get available categories for dropdown (marketplace feature)
 * Fetches from categories API with feature=marketplace filter
 */
export async function getMarketplaceCategories(): Promise<CategoryOption[]> {
  try {
    const categories = await getCategories('marketplace');
    return categories.map(cat => ({
      id: cat.id,
      name: cat.name_en,
      name_km: cat.name_km,
      name_fr: cat.name_fr,
    }));
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getMarketplaceCategories' },
      });
    }
    return [];
  }
}

// =============================================================================
// Fraud Detection API (via Cloudflare API Gateway)
// =============================================================================

/**
 * Get fraud detection statistics
 * Replaces old client-side analysis with API Gateway endpoint
 */
export async function getFraudStatistics(): Promise<ServiceResult<ApiFraudStats>> {
  try {
    const stats = await getFraudStats();
    return { success: true, data: stats };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getFraudStatistics' },
      });
    }
    return { success: false, error: 'Failed to load fraud statistics' };
  }
}

/**
 * Get list of flagged items from fraud detection
 * Replaces old findGeographicClusters and findHighVolumeSellers functions
 */
export async function getFlaggedItems(): Promise<ServiceResult<ApiFraudFlag[]>> {
  try {
    const flags = await listFlagged();
    return { success: true, data: flags };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getFlaggedItems' },
      });
    }
    return { success: false, error: 'Failed to load flagged items' };
  }
}

/**
 * Get list of blocked users/IPs
 */
export async function getBlockedEntities(): Promise<ServiceResult<ApiFraudBlock[]>> {
  try {
    const blocked = await listBlocked();
    return { success: true, data: blocked };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getBlockedEntities' },
      });
    }
    return { success: false, error: 'Failed to load blocked entities' };
  }
}

/**
 * Block a user or IP
 */
export async function blockUserOrIp(
  type: 'user' | 'ip',
  id: string,
  reason: string,
  durationHours?: number
): Promise<ServiceResult<void>> {
  try {
    await blockEntity({ type, id, reason, duration: durationHours });

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: `Blocked ${type}: ${id}`,
      level: 'info',
      data: { type, id, reason, durationHours },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'blockUserOrIp' },
        extra: { type, id, reason },
      });
    }
    return { success: false, error: `Failed to block ${type}` };
  }
}

/**
 * Unblock a user or IP
 */
export async function unblockUserOrIp(
  type: 'user' | 'ip',
  id: string
): Promise<ServiceResult<void>> {
  try {
    await unblockEntity(type, id);

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: `Unblocked ${type}: ${id}`,
      level: 'info',
      data: { type, id },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'unblockUserOrIp' },
        extra: { type, id },
      });
    }
    return { success: false, error: `Failed to unblock ${type}` };
  }
}

/**
 * Review a flagged item (clear or block)
 */
export async function reviewFlaggedItem(
  flagId: string,
  action: ReviewAction,
  notes?: string
): Promise<ServiceResult<void>> {
  try {
    await reviewFlag({ id: flagId, action, notes });

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: `Reviewed flag ${flagId}: ${action}`,
      level: 'info',
      data: { flagId, action, notes },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'reviewFlaggedItem' },
        extra: { flagId, action },
      });
    }
    return { success: false, error: 'Failed to review flagged item' };
  }
}

/**
 * Get fraud detection configuration
 */
export async function getFraudConfiguration(): Promise<ServiceResult<FraudConfig>> {
  try {
    const config = await getFraudConfig();
    return { success: true, data: config };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'getFraudConfiguration' },
      });
    }
    return { success: false, error: 'Failed to load fraud configuration' };
  }
}

/**
 * Update fraud detection configuration
 */
export async function updateFraudConfiguration(
  config: Partial<FraudConfig>
): Promise<ServiceResult<FraudConfig>> {
  try {
    const updated = await updateFraudConfig(config);

    Sentry.addBreadcrumb({
      category: 'marketplace-admin',
      message: 'Updated fraud configuration',
      level: 'info',
      data: { updatedFields: Object.keys(config) },
    });

    return { success: true, data: updated };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-admin', action: 'updateFraudConfiguration' },
        extra: { updatedFields: Object.keys(config) },
      });
    }
    return { success: false, error: 'Failed to update fraud configuration' };
  }
}

// =============================================================================
// DEPRECATED: Legacy Client-Side Fraud Detection Functions
// =============================================================================
// These functions are deprecated and replaced by API Gateway endpoints.
// The fraud detection is now handled by the Cloudflare Fraud Detection Worker
// which runs nightly at 2 AM UTC. Results are queried via getFlaggedItems().
//
// Old functions removed:
// - findGeographicClusters() -> Use getFlaggedItems() instead
// - findHighVolumeSellers() -> Use getFlaggedItems() instead  
// - getItemsByIds() -> Use getListing() for individual items
// - getItemsBySellers() -> Use listListings() with seller filter
// =============================================================================
